import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, CheckCircle2, Circle, Clock } from 'lucide-react';
import HoursProgressBar from './HoursProgressBar';
import type { MilestoneStatus } from '../types';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

interface MilestoneProgressItem {
  id: string;
  name: string;
  status: MilestoneStatus;
  dueDate?: string;
  ticketsTotal: number;
  ticketsClosed: number;
  estimatedHours: number;
  actualHours: number;
}

interface MilestoneProgressWidgetProps {
  milestones: MilestoneProgressItem[];
}

const statusIcon: Record<MilestoneStatus, typeof Circle> = {
  pending: Circle,
  in_progress: Clock,
  completed: CheckCircle2,
};

const statusColors: Record<MilestoneStatus, string> = {
  pending: 'text-surface-400',
  in_progress: 'text-info-500',
  completed: 'text-success-500',
};

export default function MilestoneProgressWidget({ milestones }: MilestoneProgressWidgetProps) {
  const { t } = useTranslation();

  if (milestones.length === 0) {
    return (
      <div className="card">
        <h3 className="text-sm font-semibold text-surface-700 mb-3">{t('charts.milestones')}</h3>
        <p className="text-xs text-surface-400 text-center py-4">{t('charts.noMilestones')}</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h3 className="text-sm font-semibold text-surface-700 mb-4">{t('charts.milestones')}</h3>
      <div className="space-y-3">
        {milestones.map((ms) => {
          const StatusIcon = statusIcon[ms.status];
          const taskPct = ms.ticketsTotal > 0 ? Math.round((ms.ticketsClosed / ms.ticketsTotal) * 100) : 0;
          const isOverdue = ms.dueDate && new Date(ms.dueDate) < new Date() && ms.status !== 'completed';

          return (
            <Link key={ms.id} to={`/milestones/${ms.id}`}
              className="block p-3 rounded-lg border border-surface-400/10 hover:border-primary-200 hover:bg-primary-50/30 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <StatusIcon size={16} className={statusColors[ms.status]} />
                  <span className="text-sm font-medium text-surface-800">{ms.name}</span>
                </div>
                <span className="text-xs text-surface-500">{taskPct}%</span>
              </div>

              <div className="flex items-center gap-3 text-xs text-surface-500 mb-2">
                <span>{ms.ticketsClosed}/{ms.ticketsTotal}{t('charts.tasks')}</span>
                {ms.dueDate && (
                  <span className={`flex items-center gap-1 ${isOverdue ? 'text-danger-600 font-medium' : ''}`}>
                    <Calendar size={10} />
                    {format(new Date(ms.dueDate), 'dd/MM/yyyy', { locale: he })}
                  </span>
                )}
              </div>

              {ms.estimatedHours > 0 && (
                <HoursProgressBar actual={ms.actualHours} estimated={ms.estimatedHours} showLabel={false} />
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
